import { PaymentStatus } from '@/api/enumerations';
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  TextFieldProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { ChangeEvent, useEffect, useState } from 'react';

import {
  menuItemDefaultStyle,
  StyledTextField,
} from '../../../../components/UI/Typography';
import { Constants } from '../../../../constants/finance';
import {
  IconDeleteMS,
  IconSearchMS,
  IconTableViewMS,
} from '../../../../constants/icons';
import { selectPaymentStatus } from '../../../../constants/selectOptions';
import {
  CSVButton,
  FiltersContainer,
  SearchBox,
  StyledFormLabel,
  StyledInputBase,
  StyledPaper,
} from './styles';
import 'dayjs/locale/pt-br';

interface FiltersProps {
  searchStatus: PaymentStatus;
  setSearchStatus: (value: PaymentStatus) => void;
  setSearchPeriod: (value: string) => void;
  setSearchTerm: (value: string) => void;
  pendingOnly: boolean;
  setPendingOnly: (value: boolean) => void;
  isHistoryTab: boolean;
  getCSVFile: () => Promise<void>;
  loadingExport: boolean;
  initialDate: string;
}

export function Filters({
  searchStatus,
  setSearchStatus,
  setSearchPeriod,
  setSearchTerm,
  pendingOnly,
  setPendingOnly,
  isHistoryTab,
  getCSVFile,
  loadingExport,
  initialDate,
}: FiltersProps): JSX.Element {
  const [selectedDate, setSelectedDate] = useState(dayjs(`${initialDate}-01`));

  const disableStatusFilter = pendingOnly && isHistoryTab;

  useEffect(() => {
    if (initialDate) {
      setSelectedDate(dayjs(`${initialDate}-01`));
    }
  }, [initialDate]);

  const updateSearchPeriod = (date: Dayjs): void => {
    setSearchPeriod(date.format('YYYY-MM'));
  };

  const handleMonthChange = (newDate: Dayjs | null): void => {
    if (newDate && selectedDate) {
      const updatedDate = newDate.year(selectedDate.year());
      setSelectedDate(updatedDate);
      updateSearchPeriod(updatedDate);
    }
  };

  const handleYearChange = (newDate: Dayjs | null): void => {
    if (newDate && selectedDate) {
      const updatedDate = selectedDate.year(newDate.year());
      setSelectedDate(updatedDate);
      updateSearchPeriod(updatedDate);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setPendingOnly(event.target.value === 'true');
  };
  return (
    <SearchBox>
      {isHistoryTab && (
        <FormControl component="fieldset" sx={{ mb: '32px' }}>
          <RadioGroup
            row
            value={String(pendingOnly)}
            onChange={handleChange}
            sx={{ gap: 2 }}
          >
            <StyledPaper variant="outlined" isActive={pendingOnly === false}>
              <StyledFormLabel
                value="false"
                control={<Radio />}
                label="Por período"
                isActive={pendingOnly === false}
              />
              <Box display="flex" gap={2} padding="0 20px 20px">
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="pt-br"
                >
                  <DatePicker
                    disabled={pendingOnly}
                    label={Constants.month}
                    value={selectedDate}
                    views={['month']}
                    format="MMMM"
                    slotProps={{
                      calendarHeader: {},
                    }}
                    maxDate={dayjs().subtract(1, 'month')}
                    slots={{
                      textField:
                        StyledTextField as React.ComponentType<TextFieldProps>,
                    }}
                    sx={{ width: '164px' }}
                    onChange={handleMonthChange}
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    disabled={pendingOnly}
                    label={Constants.year}
                    value={selectedDate}
                    views={['year']}
                    format="YYYY"
                    maxDate={dayjs()}
                    slots={{
                      textField:
                        StyledTextField as React.ComponentType<TextFieldProps>,
                    }}
                    sx={{ width: '164px' }}
                    onChange={handleYearChange}
                  />
                </LocalizationProvider>
              </Box>
            </StyledPaper>
            <StyledPaper variant="outlined" isActive={pendingOnly === true}>
              <StyledFormLabel
                value="true"
                control={<Radio />}
                label="Pendentes"
                isActive={pendingOnly === true}
              />
              <Typography padding="0 20px 20px">
                {Constants.allHistory}
              </Typography>
            </StyledPaper>
          </RadioGroup>
        </FormControl>
      )}
      <Box display="flex" justifyContent="space-between">
        <FiltersContainer>
          <StyledInputBase
            placeholder={Constants.searchPlaceholder}
            inputProps={{ 'aria-label': 'pesquisar', maxLength: 20 }}
            type="search"
            startAdornment={
              <InputAdornment
                position="start"
                sx={(theme) => ({ color: theme.palette.secondary.main })}
              >
                {IconSearchMS}
              </InputAdornment>
            }
            onChange={(e) => {
              if (e.target.value.length > 0) setSearchTerm(e.target.value);
              if (e.target.value.length === 0) setSearchTerm('');
            }}
            onFocus={(e) => {
              e.target.value = '';
              setSearchTerm('');
            }}
          />
          <StyledTextField
            id="status"
            select
            disabled={disableStatusFilter}
            label={Constants.status}
            value={searchStatus}
            onChange={(e) =>
              setSearchStatus(Number(e.target.value) as PaymentStatus)
            }
            sx={{ width: '260px' }}
          >
            {selectPaymentStatus().map((role) => (
              <MenuItem
                key={role.value}
                value={role.value}
                sx={{ ...menuItemDefaultStyle }}
              >
                {role.label}
              </MenuItem>
            ))}
          </StyledTextField>
          <Box display="flex" alignItems="center" gap="8px">
            <Tooltip title={Constants.clearSearch}>
              <IconButton
                disabled={disableStatusFilter}
                onClick={() => {
                  setSearchPeriod('');
                  setSearchStatus(PaymentStatus.SELECT);
                }}
              >
                {IconDeleteMS}
              </IconButton>
            </Tooltip>
          </Box>
        </FiltersContainer>
        <CSVButton disabled={loadingExport} onClick={getCSVFile}>
          {loadingExport ? (
            <CircularProgress size={22} />
          ) : (
            <>{IconTableViewMS} exportar CSV</>
          )}
        </CSVButton>
      </Box>
    </SearchBox>
  );
}
